import React, { createContext, useContext, useEffect, useState } from 'react'
import { GrowthBookProvider as GBProvider, useGrowthBook as useGBHook } from '@growthbook/growthbook-react'
import { growthbook, loadGrowthBookFeatures } from '../lib/growthbook'
import { useAuth } from '../hooks/useAuth'

/**
 * True after the latest `loadGrowthBookFeatures` run has finished (success or failure).
 * Until then, `growthbook.isOn(...)` can be false even when the flag is on in GrowthBook
 * because the feature payload has not been applied yet.
 */
const GrowthBookFeaturesReadyContext = createContext(false)

export const useGrowthBookFeaturesReady = () => useContext(GrowthBookFeaturesReadyContext)

export const GrowthBookProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { country, user } = useAuth()
  const [featuresReady, setFeaturesReady] = useState(false)

  useEffect(() => {
    let cancelled = false
    setFeaturesReady(false)
    void (async () => {
      try {
        await loadGrowthBookFeatures(country)
      } finally {
        if (!cancelled) {
          setFeaturesReady(true)
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [country, user]) // Reload when country or user changes

  // Always pass the growthbook instance, even if not fully loaded yet
  return (
    <GrowthBookFeaturesReadyContext.Provider value={featuresReady}>
      <GBProvider growthbook={growthbook}>{children}</GBProvider>
    </GrowthBookFeaturesReadyContext.Provider>
  )
}

// Re-export the official GrowthBook hook
export const useGrowthBook = useGBHook
